import type {
  CurrentThreadPositioning,
  DiffSide,
  OriginalThreadPositioning,
  StaticDiffLine,
  Thread,
} from '@github-ui/diff-lines/conversation-types'
import {
  fileCurrentPositioning,
  positioningToLineKey,
  toThreadSubjectType,
} from '@github-ui/diff-lines/thread-positioning'
import {fileLevelCommentKey, type FileKey, type LineKey} from '@github-ui/diff-lines/types'
import type {
  DiffLine,
  PositionedThread,
} from '@github-ui/dotcom-schema/types/pull_requests/v1/thread_preview_positioning'
import type {CreateReviewCommentMutationData} from '../mutations/use-create-review-comment-mutation'
import type {ThreadMarker} from '../page-data/payloads/file-tree'

/** Markers store key for threads that cannot be positioned in the diff being viewed. */
export const OUTSIDE_DIFF_KEY = '__OUTSIDE_DIFF__' as FileKey

/** Whether the thread is outdated across the full range of the pull request, for use outside of the diff. */
export function isThreadOutdated(thread: Pick<Thread, 'originalPositioning'>): boolean {
  return thread.originalPositioning?.isOutdated ?? false
}

/**
 * Store buckets describe the diff being viewed, so original positioning must not be used here.
 * Current positions without lines use the file-level marker, including unpositionable line threads.
 */
export function getThreadMarkerKeys(thread: Pick<Thread, 'currentPositioning'>): {
  filePath: string
  lineKey: LineKey | FileKey
} {
  const current = thread.currentPositioning
  if (!current) return {filePath: OUTSIDE_DIFF_KEY, lineKey: OUTSIDE_DIFF_KEY}

  return {filePath: current.path, lineKey: positioningToLineKey(current) ?? fileLevelCommentKey}
}

/** Builds current positioning from the viewed comparison's file-tree markers. */
export function buildCurrentPositioningFromMarker(
  markerKey: string,
  threadMarker: ThreadMarker,
  path: string,
): CurrentThreadPositioning | undefined {
  const isOutdated = !!threadMarker.outdatedReason
  if (markerKey === 'FILE') return fileCurrentPositioning(path, isOutdated)

  const endSide = markerKey[0]
  if (!isLineSideLetter(endSide)) return undefined

  const endLine = Number(markerKey.slice(1))
  const endDiffSide = diffSideMap[endSide]
  const startSide = threadMarker.start?.[0]

  return {
    path,
    startLine: threadMarker.start ? parseInt(threadMarker.start.slice(1)) : endLine,
    startDiffSide: threadMarker.start ? (isLineSideLetter(startSide) ? diffSideMap[startSide] : 'RIGHT') : endDiffSide,
    endLine,
    endDiffSide,
    isOutdated,
  }
}

/**
 * Derives original positioning from the preview endpoint, whose outdatedness is scoped to the PR head.
 * An omitted path can reuse only a previous original path, never a current position.
 */
export function positionedThreadToOriginalPositioning(
  thread: PositionedThread,
  existingOriginalPath?: string,
): OriginalThreadPositioning {
  return {
    path: thread.path ?? existingOriginalPath ?? '',
    startLine: thread.startLine ?? null,
    startDiffSide: (thread.startDiffSide as DiffSide | undefined) ?? null,
    endLine: thread.endLine ?? null,
    endDiffSide: (thread.endDiffSide as DiffSide | undefined) ?? null,
    diffLines: thread.diffLines.map(protoDiffLineToStaticDiffLine),
    isOutdated: thread.isOutdated,
    originalDiffPathUri: thread.originalDiffPathUri ?? null,
  }
}

/** A newly created comment is current only in the comparison it was created on. */
export function buildCurrentPositioningFromCommentData(
  inputs: CreateReviewCommentMutationData,
): CurrentThreadPositioning {
  if (toThreadSubjectType(inputs.subjectType) === 'FILE') return fileCurrentPositioning(inputs.path, false)

  const endDiffSide = commentSideToDiffSide(inputs.side)
  return {
    path: inputs.path,
    startLine: inputs.startLine ?? inputs.line ?? null,
    startDiffSide: inputs.startSide ? commentSideToDiffSide(inputs.startSide) : endDiffSide,
    endLine: inputs.line ?? null,
    endDiffSide,
    isOutdated: false,
  }
}

function commentSideToDiffSide(side: string | undefined): DiffSide {
  return side?.toLowerCase() === 'right' ? 'RIGHT' : 'LEFT'
}

const diffSideMap = {L: 'LEFT', R: 'RIGHT'} as const

function isLineSideLetter(value: string | undefined): value is keyof typeof diffSideMap {
  return value === 'L' || value === 'R'
}

function protoDiffLineToStaticDiffLine(line: DiffLine): StaticDiffLine {
  return {
    html: line.html ?? '',
    left: line.left ?? null,
    right: line.right ?? null,
    text: line.text ?? '',
    type: line.type as StaticDiffLine['type'],
  }
}
